import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
const toast = { success: (m) => console.log("[toast]", m), error: (m) => console.error("[toast]", m), info: (m) => console.log("[toast]", m) };
import PageContainer, { PageHeader } from "@/components/PageContainer";
import api from "@/lib/assessments";
import { readAll } from "@/lib/assessments";
const formatApiError = (e) => (e && e.message) || "Unknown error";
import { Save, ChevronLeft } from "lucide-react";
import { ORS_ITEMS, SRS_ITEMS, interpretORS, interpretSRS } from "@/data/instruments";

/**
 * Analog scale (0-10) instrument used for ORS and SRS.
 * kind: "ors" | "srs"
 */
export default function AnalogInstrument({ kind }) {
  const navigate = useNavigate();
  const isORS = kind === "ors";
  const items = isORS ? ORS_ITEMS : SRS_ITEMS;
  const title = isORS ? "Outcome Rating Scale (ORS)" : "Session Rating Scale (SRS)";
  const overline = "Portal · Tool";
  const subtitle = isORS
    ? "Ultra-brief 4-item outcome measure administered at the start of every session (FIT non-negotiable)."
    : "Ultra-brief 4-item alliance measure administered at the end of every session (FIT non-negotiable).";
  const interpret = isORS ? interpretORS : interpretSRS;
  const testidPrefix = kind;

  const [patient, setPatient] = useState("");
  const [notes, setNotes] = useState("");
  const [values, setValues] = useState(() => Object.fromEntries(items.map((it) => [it.id, 5])));
  const [saving, setSaving] = useState(false);

  const total = Object.values(values).reduce((a, b) => a + Number(b), 0);
  const interp = interpret(total);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/assessments", {
        kind,
        patient_identifier: patient || null,
        responses: values,
        score: total,
        interpretation: interp.band,
        notes: notes || null,
      });
      toast.success(`${isORS ? "ORS" : "SRS"} saved to history.`);
      navigate("/portal/continuum/tools/history");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer testid={`${testidPrefix}-page`}>
      <Link
        to="/portal/continuum/tools"
        className="text-sm text-ink-muted hover:text-navy inline-flex items-center gap-1 mb-4"
        data-testid={`${testidPrefix}-back`}
      >
        <ChevronLeft size={14} strokeWidth={2} /> All tools
      </Link>

      <PageHeader overline={overline} title={title} subtitle={subtitle} />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="card p-5">
            <label className="label">Patient identifier</label>
            <input
              className="field"
              value={patient}
              onChange={(e) => setPatient(e.target.value)}
              placeholder="Initials or MRN"
              data-testid={`${testidPrefix}-patient`}
            />
          </div>

          <div className="card p-5 space-y-6">
            {items.map((it) => (
              <div key={it.id} data-testid={`${testidPrefix}-item-${it.id}`}>
                <div className="flex items-baseline justify-between mb-1">
                  <div className="text-navy font-medium">{it.label}</div>
                  <div className="text-teal font-semibold text-lg">{values[it.id]}</div>
                </div>
                {it.hint && <div className="text-xs text-ink-muted mb-2">{it.hint}</div>}
                <input
                  type="range"
                  min={0}
                  max={10}
                  step={0.5}
                  value={values[it.id]}
                  onChange={(e) => setValues({ ...values, [it.id]: Number(e.target.value) })}
                  className="w-full accent-teal"
                  data-testid={`${testidPrefix}-slider-${it.id}`}
                />
                <div className="flex justify-between text-xs text-ink-muted mt-1">
                  <span>{it.low}</span>
                  <span>{it.high}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="card p-5">
            <label className="label">Notes (optional)</label>
            <textarea
              className="field min-h-[80px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid={`${testidPrefix}-notes`}
            />
          </div>
        </div>

        <aside className="lg:sticky lg:top-6 self-start">
          <div className="card p-6" data-testid={`${testidPrefix}-summary`}>
            <div className="overline mb-2">Total</div>
            <div className="text-5xl font-semibold text-navy">
              {total}
              <span className="text-xl text-ink-muted"> / 40</span>
            </div>
            <div className="mt-4">
              <div className="badge badge-teal mb-2">{interp.band}</div>
              <p className="text-sm text-ink leading-relaxed">{interp.detail}</p>
            </div>

            <div className="rule my-4" />

            <button
              onClick={save}
              disabled={saving}
              className="btn btn-primary w-full justify-center"
              data-testid={`${testidPrefix}-save`}
            >
              {saving ? "Saving..." : (
                <>
                  <Save size={14} strokeWidth={2} /> Save to history
                </>
              )}
            </button>
          </div>
        </aside>
      </div>
    </PageContainer>
  );
}
